import { RxDatabase, createRxDatabase, addRxPlugin } from 'rxdb'
import { wrappedValidateAjvStorage } from 'rxdb/plugins/validate-ajv'
import { RxDBDevModePlugin } from 'rxdb/plugins/dev-mode'
import { getRxStorageDexie } from 'rxdb/plugins/storage-dexie'
import { RxDBMigrationSchemaPlugin } from 'rxdb/plugins/migration-schema'
import { ExperienceCollection, experienceSchema } from 'src/system/content/schemas/experience'
import { FolderCollection, folderDocMethods, folderSchema } from 'src/system/content/schemas/folder'
import { LogCollection, logDocMethods, logSchema } from 'src/system/content/schemas/log'
import {
  MessageCollection,
  messageDocMethods,
  messageMigrationStrategies,
  messageSchema,
} from 'src/system/content/schemas/message'
import {
  MessageRecipientCollection,
  messageRecipientDocMethods,
  messageRecipientSchema,
} from 'src/system/content/schemas/messageRecipient'
import { PackCollection, packDocMethods, packMigrationStrategies, packSchema } from 'src/system/content/schemas/pack'
import { PackFooterCollection, packFooterSchema } from 'src/system/content/schemas/packFooter'
import { PackHeaderCollection, packHeaderSchema } from 'src/system/content/schemas/packHeader'
import { ResourceCollection, resourceDocMethods, resourceSchema } from 'src/system/content/schemas/resource'
import { SignatureCollection, signatureSchema } from 'src/system/content/schemas/signature'
import { UserCollection, userMigrationStrategies, userSchema } from 'src/system/content/schemas/user'
import ILocalContentService from 'src/system/content/services/ILocalContentService'
import { QuoteCollection, quoteDocMethods, quoteSchema } from 'src/system/content/schemas/quote'

export type DatabaseCollections = {
  folders: FolderCollection
  resources: ResourceCollection
  packs: PackCollection
  messages: MessageCollection
  users: UserCollection
  signatures: SignatureCollection
  messageRecipients: MessageRecipientCollection
  packHeaders: PackHeaderCollection
  packFooters: PackFooterCollection
  logs: LogCollection
  experiences: ExperienceCollection
  quotes: QuoteCollection
}

export type Database = RxDatabase<DatabaseCollections>

export default class RxDBLocalContentService implements ILocalContentService {
  private db: Database | undefined

  async init(): Promise<void> {
    if (this.db !== undefined) {
      return
    }

    addRxPlugin(RxDBMigrationSchemaPlugin)

    if (import.meta.env.MODE === 'development') {
      addRxPlugin(RxDBDevModePlugin)
    }

    const storage =
      import.meta.env.MODE === 'development'
        ? wrappedValidateAjvStorage({
            storage: getRxStorageDexie(),
          })
        : getRxStorageDexie()

    this.db = await createRxDatabase({
      name: 'velociti',
      storage,
    })

    await this.db.addCollections({
      folders: {
        schema: folderSchema,
        methods: folderDocMethods,
      },
      resources: {
        schema: resourceSchema,
        methods: resourceDocMethods,
      },
      packs: {
        schema: packSchema,
        methods: packDocMethods,
        migrationStrategies: packMigrationStrategies,
        autoMigrate: true,
      },
      messages: {
        schema: messageSchema,
        methods: messageDocMethods,
        migrationStrategies: messageMigrationStrategies,
      },
      users: {
        schema: userSchema,
        migrationStrategies: userMigrationStrategies,
      },
      signatures: {
        schema: signatureSchema,
      },
      messageRecipients: {
        schema: messageRecipientSchema,
        methods: messageRecipientDocMethods,
      },
      packHeaders: {
        schema: packHeaderSchema,
      },
      packFooters: {
        schema: packFooterSchema,
      },
      logs: {
        schema: logSchema,
        methods: logDocMethods,
      },
      experiences: {
        schema: experienceSchema,
      },
      quotes: {
        schema: quoteSchema,
        methods: quoteDocMethods,
      },
    })
  }

  getDatabase(): Database {
    if (this.db === undefined) {
      throw new Error('Trying to access DB without initializing')
    }

    return this.db
  }
}
